import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  AreaChart, Area, ComposedChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';
import { useSiteActions, actionMarkers, groupMarkersByDate, ActionTooltipRows, QuickActionModal } from './SiteActionsOverlay';
import gaApi from '../services/google-analytics';
import concessionsApi from '../services/concessions';
import GoogleAdsChat from './GoogleAdsChat';
import './GoogleAnalytics.css';
import './GoogleAds.css'; // réutilise les styles d'audit (gads-audit-*, gads-reco-*, …)

// ── Utils dates ────────────────────────────────────────────────────────────────

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function computeRange(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const back = (n) => { const d = new Date(today); d.setDate(d.getDate() - n); return d; };
  switch (preset) {
    case '7j':   return { start: toISODate(back(6)),  end: toISODate(today) };
    case '28j':  return { start: toISODate(back(27)), end: toISODate(today) };
    case '90j':  return { start: toISODate(back(89)), end: toISODate(today) };
    case 'mois-en-cours': {
      const d = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: toISODate(d), end: toISODate(today) };
    }
    case 'mois-dernier': {
      const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const f = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: toISODate(d), end: toISODate(f) };
    }
    case '12m': {
      const d = new Date(today.getFullYear(), today.getMonth() - 11, 1);
      return { start: toISODate(d), end: toISODate(today) };
    }
    default: return { start: toISODate(back(27)), end: toISODate(today) };
  }
}

const PRESETS = [
  { id: '7j',            label: '7 jours' },
  { id: '28j',           label: '28 jours' },
  { id: '90j',           label: '90 jours' },
  { id: 'mois-en-cours', label: 'Mois en cours' },
  { id: 'mois-dernier',  label: 'Mois dernier' },
  { id: '12m',           label: '12 mois' },
  { id: 'custom',        label: 'Personnalisé' },
];

// ── Utils format ───────────────────────────────────────────────────────────────

const nf = new Intl.NumberFormat('fr-FR');
const nf1 = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

function fmtNumber(n) { return nf.format(Math.round(n || 0)); }
function fmtCompact(n) {
  n = n || 0;
  if (Math.abs(n) >= 1000000) return nf1.format(n / 1000000) + ' M';
  if (Math.abs(n) >= 1000) return nf1.format(n / 1000) + ' k';
  return nf.format(Math.round(n));
}
function fmtDecimal(n) { return nf1.format(n || 0); }
function fmtPercent(n) { return nf1.format((n || 0) * 100) + ' %'; }
function fmtDuration(s) {
  s = Math.round(s || 0);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m === 0) return `${sec} s`;
  return `${m} min ${String(sec).padStart(2, '0')} s`;
}
function fmtDelta(d) {
  if (d == null) return '—';
  const pct = d * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${nf1.format(pct)} %`;
}
// '20260612' → '12/06'
function fmtDay(s) {
  if (!s || s.length !== 8) return s;
  return `${s.slice(6, 8)}/${s.slice(4, 6)}`;
}

// ── Config KPIs ──────────────────────────────────────────────────────────────

const KPI_DEFS = [
  { key: 'activeUsers',               label: 'Utilisateurs',         fmt: fmtNumber,   series: 'activeUsers' },
  { key: 'newUsers',                  label: 'Nouveaux utilisateurs', fmt: fmtNumber,  series: 'newUsers' },
  { key: 'sessions',                  label: 'Sessions',             fmt: fmtNumber,   series: 'sessions' },
  { key: 'screenPageViews',           label: 'Pages vues',           fmt: fmtNumber,   series: 'screenPageViews' },
  { key: 'averageSessionDuration',    label: 'Durée moy. session',   fmt: fmtDuration },
  { key: 'engagementRate',            label: "Taux d'engagement",    fmt: fmtPercent },
  { key: 'bounceRate',                label: 'Taux de rebond',       fmt: fmtPercent,  lowerIsBetter: true },
  { key: 'screenPageViewsPerSession', label: 'Pages / session',      fmt: fmtDecimal },
  { key: 'eventCount',                label: 'Événements',           fmt: fmtNumber },
  { key: 'conversions',               label: 'Conversions',          fmt: fmtNumber },
];

const PALETTE = ['#0891b2', '#3b82f6', '#f59e0b', '#22c55e', '#a855f7', '#ef4444', '#14b8a6', '#ec4899', '#64748b', '#84cc16'];

const MAIN_METRICS = [
  { key: 'sessions',        label: 'Sessions',     color: '#0891b2' },
  { key: 'activeUsers',     label: 'Utilisateurs', color: '#3b82f6' },
  { key: 'screenPageViews', label: 'Pages vues',   color: '#f59e0b' },
  { key: 'newUsers',        label: 'Nouveaux',     color: '#22c55e' },
];

const LS_PROP = 'crm-ga-property';
const LS_PRESET = 'crm-ga-preset';

// ── Composants atomiques ─────────────────────────────────────────────────────

function KpiCard({ def, kpi, chartData }) {
  if (!kpi) return null;
  const positive = def.lowerIsBetter ? (kpi.delta != null && kpi.delta < 0) : (kpi.delta != null && kpi.delta > 0);
  const negative = def.lowerIsBetter ? (kpi.delta != null && kpi.delta > 0) : (kpi.delta != null && kpi.delta < 0);
  const deltaClass = kpi.delta == null ? 'flat' : positive ? 'up' : negative ? 'down' : 'flat';
  const spark = def.series && chartData && chartData.length > 1
    ? chartData.map(p => ({ v: p[def.series] || 0 })) : null;
  return (
    <div className="ga-kpi">
      <div className="ga-kpi-label">{def.label}</div>
      <div className="ga-kpi-value">{def.fmt(kpi.value)}</div>
      <div className="ga-kpi-foot">
        <span className={`ga-delta ${deltaClass}`}>
          {kpi.delta != null && (positive ? '▲' : negative ? '▼' : '')} {fmtDelta(kpi.delta)}
        </span>
        {spark && (
          <span className="ga-spark">
            <ResponsiveContainer width="100%" height={28}>
              <AreaChart data={spark} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                <Area type="monotone" dataKey="v" stroke="var(--color-accent)" fill="var(--color-accent-light)" strokeWidth={1.5} dot={false} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </span>
        )}
      </div>
    </div>
  );
}

function Panel({ title, subtitle, children, className = '' }) {
  return (
    <div className={`ga-panel ${className}`}>
      <div className="ga-panel-head">
        <h3>{title}</h3>
        {subtitle && <span className="ga-panel-sub">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

function BreakdownTable({ rows, cols, empty = 'Aucune donnée' }) {
  if (!rows || rows.length === 0) return <div className="ga-empty">{empty}</div>;
  return (
    <table className="ga-table">
      <thead>
        <tr>
          <th>{cols.labelHead || ''}</th>
          {cols.metrics.map(m => <th key={m.key} className="num">{m.head}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td className="ga-td-label" title={r.label}>{r.label}</td>
            {cols.metrics.map(m => <td key={m.key} className="num">{m.fmt(r[m.key])}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DonutChart({ data, dataKey, height = 240 }) {
  const total = (data || []).reduce((s, d) => s + (d[dataKey] || 0), 0);
  if (!data || data.length === 0) return <div className="ga-empty">Aucune donnée</div>;
  return (
    <div className="ga-donut-wrap">
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={data} dataKey={dataKey} nameKey="label" cx="50%" cy="50%"
            innerRadius={55} outerRadius={85} paddingAngle={2} stroke="var(--bg-surface)" strokeWidth={2}>
            {data.map((d, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip content={<DonutTooltip total={total} />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="ga-legend">
        {data.slice(0, 8).map((d, i) => (
          <div className="ga-legend-row" key={i}>
            <span className="ga-legend-dot" style={{ background: PALETTE[i % PALETTE.length] }} />
            <span className="ga-legend-label" title={d.label}>{d.label}</span>
            <span className="ga-legend-val">{total ? Math.round((d[dataKey] / total) * 100) : 0} %</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DonutTooltip({ active, payload, total }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  return (
    <div className="ga-tooltip">
      <div className="ga-tt-title">{p.name}</div>
      <div>{fmtNumber(p.value)} ({total ? Math.round((p.value / total) * 100) : 0} %)</div>
    </div>
  );
}

function MainChartTooltip({ active, payload, label, metricLabel, compare, actionsByDate }) {
  if (!active || !payload || !payload.length) return null;
  const cur = payload.find(p => p.dataKey === 'value');
  const prev = payload.find(p => p.dataKey === 'prev');
  return (
    <div className="ga-tooltip">
      <div className="ga-tt-title">{fmtDay(label)}</div>
      <div><span className="ga-tt-dot" style={{ background: 'var(--color-accent)' }} />{metricLabel} : <b>{fmtNumber(cur?.value)}</b></div>
      {compare && prev && (
        <div className="muted"><span className="ga-tt-dot" style={{ background: 'var(--text-muted)' }} />Période préc. : {fmtNumber(prev.value)}</div>
      )}
      <ActionTooltipRows items={actionsByDate && actionsByDate[label]} />
    </div>
  );
}

// ── Onglet Audit IA (santé de la mesure) ─────────────────────────────────────

function gaScoreColor(s) {
  if (s == null || Number.isNaN(s)) return 'var(--text-muted)';
  if (s >= 70) return 'var(--color-success)';
  if (s >= 45) return 'var(--color-warning)';
  return 'var(--color-error)';
}
const GA_DIM_LABELS = {
  tracking: 'Conversions & key events', data_quality: 'Qualité des données', retention: 'Rétention',
  structure: 'Structure de mesure', integrations: 'Intégrations', acquisition: 'Acquisition / canaux', engagement: 'Engagement',
};
const GA_PRIO = { P1: { label: 'Prioritaire', cls: 'gads-prio-p1' }, P2: { label: 'Important', cls: 'gads-prio-p2' }, P3: { label: 'Optimisation', cls: 'gads-prio-p3' } };
const GA_EFFORT = { faible: 'gads-eff-low', moyen: 'gads-eff-mid', 'élevé': 'gads-eff-high', 'élevée': 'gads-eff-high' };
const GA_SUGGESTIONS = [
  "Quels sont les principaux problèmes de mesure de cette propriété ?",
  "Mes conversions sont-elles bien suivies ?",
  "Montre-moi la répartition des canaux d'acquisition.",
  "Pourquoi la part de trafic « Direct » est-elle un problème ?",
];

function GaAuditTab({ property, propertyName }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const fetchAudit = useCallback(async () => {
    if (!property) return null;
    try { const { data } = await gaApi.getAudit(property); setAudit(data); return data; }
    catch (e) { setError(e.response?.data?.error || e.message); return null; }
  }, [property]);

  const startPolling = useCallback(() => {
    setRunning(true);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const d = await fetchAudit();
      if (d && d.status !== 'running') { clearInterval(pollRef.current); setRunning(false); }
    }, 5000);
  }, [fetchAudit]);

  useEffect(() => {
    setAudit(null); setError(null); setRunning(false); setLoading(true);
    fetchAudit().then(d => { setLoading(false); if (d && d.status === 'running') startPolling(); });
    return () => clearInterval(pollRef.current);
  }, [property, fetchAudit, startPolling]);

  const launch = async () => {
    setError(null); setRunning(true);
    try { await gaApi.runAudit(property); startPolling(); }
    catch (e) { setError(e.response?.data?.error || e.message); setRunning(false); }
  };

  if (!property) return <div className="gads-loading">Sélectionnez une propriété.</div>;
  if (loading) return <div className="gads-loading">Chargement de l'audit…</div>;

  const done = audit && audit.status === 'done';
  const isRunning = running || (audit && audit.status === 'running');
  const recos = (done && audit.recommendations) || [];
  const quickWins = recos.filter(r => r.is_quick_win);
  const scores = (done && audit.scores) || [];

  return (
    <div className="gads-audit">
      <div className="gads-audit-head">
        <div>
          <h3>🤖 Audit IA — santé de la mesure</h3>
          <p className="muted">Analyse de la configuration GA4 (conversions, rétention, qualité des données…) · {propertyName || 'propriété'} · lecture seule</p>
        </div>
        <div className="gads-ap-head-actions">
          <GoogleAdsChat
            idValue={property} idKey="property"
            endpoint="/api/google-analytics/audit-chat/stream"
            accountName={propertyName}
            suggestions={GA_SUGGESTIONS}
            toolLabels={{ interroger_donnees: '📊 Lecture des données…' }}
            fabLabel="💬 Discuter de la mesure"
            disclaimer="Lecture seule — je conseille, les réglages se font dans GA4."
          />
          <button className="page-action-btn" onClick={launch} disabled={isRunning}>
            {isRunning ? '⏳ Audit en cours…' : (done ? "↻ Relancer l'audit" : "▶ Lancer l'audit")}
          </button>
        </div>
      </div>

      {error && <div className="gads-error">⚠️ {error}</div>}
      {isRunning && <div className="gads-audit-running">L'IA analyse la configuration de mesure… ~1 minute. Vous pouvez quitter et revenir.</div>}
      {audit && audit.status === 'error' && !isRunning && <div className="gads-error">L'audit a échoué : {audit.error || 'erreur inconnue'}.</div>}
      {!audit && !isRunning && (
        <div className="gads-audit-empty"><p>Aucun audit pour cette propriété. Lancez une analyse de la santé de la mesure : suivi des conversions, rétention, qualité des données, intégrations, acquisition…</p></div>
      )}

      {done && (
        <>
          <div className="gads-audit-summary">
            <div className="gads-audit-score" style={{ borderColor: gaScoreColor(Number(audit.global_score)) }}>
              <div className="gads-audit-score-val" style={{ color: gaScoreColor(Number(audit.global_score)) }}>{Math.round(Number(audit.global_score))}</div>
              <div className="gads-audit-score-max">/ 100</div>
            </div>
            <div className="gads-audit-verdict">
              <p>{audit.verdict}</p>
              <div className="muted gads-audit-date">Audit du {new Date(audit.run_at).toLocaleString('fr-FR')} · {audit.model}</div>
            </div>
          </div>

          {quickWins.length > 0 && (
            <Panel title="⚡ Quick wins" subtitle="Fort impact, faible effort — à faire en premier">
              <div className="gads-qw-grid">
                {quickWins.map((r, i) => <div key={i} className="gads-qw-card"><strong>{r.title}</strong><span className="muted">{r.action}</span></div>)}
              </div>
            </Panel>
          )}

          <Panel title="Scores par dimension">
            <div className="gads-dim-grid">
              {scores.map((s, i) => (
                <div key={i} className="gads-dim">
                  <div className="gads-dim-top"><span>{GA_DIM_LABELS[s.dimension] || s.dimension}</span><strong style={{ color: gaScoreColor(Number(s.score)) }}>{Math.round(Number(s.score))}</strong></div>
                  <div className="gads-dim-bar"><div style={{ width: `${Math.max(0, Math.min(100, Number(s.score)))}%`, background: gaScoreColor(Number(s.score)) }} /></div>
                  <p className="muted">{s.summary}</p>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Recommandations" subtitle={`${recos.length} action(s) classée(s) par priorité`}>
            <div className="gads-reco-list">
              {recos.map((r, i) => {
                const prio = GA_PRIO[r.priority] || { label: r.priority, cls: 'gads-prio-p3' };
                return (
                  <div key={i} className="gads-reco">
                    <div className="gads-reco-head">
                      <span className={`gads-prio ${prio.cls}`}>{prio.label}</span>
                      <strong>{r.title}</strong>
                      {r.is_quick_win && <span className="gads-qw-tag">⚡ quick win</span>}
                      {r.effort && <span className={`gads-eff ${GA_EFFORT[String(r.effort).toLowerCase()] || ''}`}>effort {r.effort}</span>}
                    </div>
                    {r.finding && <p className="gads-reco-finding">📊 {r.finding}</p>}
                    {r.why && <p className="gads-reco-why">{r.why}</p>}
                    {r.action && <p className="gads-reco-action">✅ <strong>À faire :</strong> {r.action}</p>}
                    {r.impact && <p className="gads-reco-impact">📈 Impact : {r.impact}</p>}
                  </div>
                );
              })}
            </div>
          </Panel>

          {audit.report_md && (
            <Panel title="Rapport détaillé">
              <details className="gads-report"><summary>Afficher le rapport complet</summary><div className="gads-report-md">{audit.report_md}</div></details>
            </Panel>
          )}
        </>
      )}
    </div>
  );
}

// ── Composant principal ──────────────────────────────────────────────────────

// Onglet Configuration (super-admin) : affecte chaque propriété GA4 à une concession.
function GaConfigTab() {
  const [properties, setProperties] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [cfg, conc] = await Promise.all([gaApi.getConfig(), concessionsApi.getAll()]);
      setProperties(cfg.data.properties || []);
      setConcessions(conc.data.concessions || []);
    } catch (e) { setError(e.response?.data?.error || 'Erreur de chargement.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const save = async (id, concession_id) => {
    try {
      await gaApi.setPropertyConcession(id, concession_id || null);
      setProperties((prev) => prev.map((p) => (p.id === id ? { ...p, concession_id: concession_id ? Number(concession_id) : null } : p)));
    } catch (e) { setError(e.response?.data?.error || 'Enregistrement impossible.'); }
  };

  if (loading) return <div className="gads-empty">Chargement…</div>;

  return (
    <div className="gads-config">
      <p className="muted gads-config-hint">
        Affectez chaque site (propriété GA4) à une concession pour gérer les droits. Un utilisateur non transverse
        ne voit que les sites de <strong>sa</strong> concession ; les sites non affectés restent visibles des admins/super-admins.
      </p>
      {error && <div className="status-error gads-config-err">{error}</div>}
      <div className="table-container">
        <table className="gads-table">
          <thead><tr><th>Site (propriété GA4)</th><th>ID</th><th>Compte</th><th>Concession rattachée</th></tr></thead>
          <tbody>
            {properties.length === 0 && <tr><td colSpan={4} className="gads-empty">Aucune propriété.</td></tr>}
            {properties.map((p) => (
              <tr key={p.id}>
                <td><strong>{p.name}</strong></td>
                <td className="muted">{p.id}</td>
                <td className="muted">{p.accountName || '—'}</td>
                <td>
                  <select className="page-select" value={p.concession_id || ''} onChange={(e) => save(p.id, e.target.value)}>
                    <option value="">— Non affecté —</option>
                    {concessions.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GoogleAnalytics({ user, setHeaderAction }) {
  const isSuperAdmin = user?.role === 'super-admin';
  const [accounts, setAccounts] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [propsError, setPropsError] = useState(null);

  const [property, setProperty] = useState(() => localStorage.getItem(LS_PROP) || '');
  const [preset, setPreset] = useState(() => localStorage.getItem(LS_PRESET) || '28j');
  const [custom, setCustom] = useState(() => computeRange('28j'));
  const [compare, setCompare] = useState(true);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [tab, setTab] = useState('overview');
  const [mainMetric, setMainMetric] = useState('sessions');

  const [realtime, setRealtime] = useState(null);
  const rtTimer = useRef(null);

  const range = useMemo(
    () => (preset === 'custom' ? custom : computeRange(preset)),
    [preset, custom]
  );

  // Aucune action de header pour cette page (lecture seule).
  useEffect(() => { if (setHeaderAction) setHeaderAction(null); }, [setHeaderAction]);

  // ── Chargement des propriétés ──
  useEffect(() => {
    let alive = true;
    gaApi.getProperties()
      .then(({ data }) => {
        if (!alive) return;
        setConfigured(data.configured);
        setAccounts(data.accounts || []);
        const all = (data.accounts || []).flatMap(a => a.properties);
        if (all.length && (!property || !all.some(p => p.id === property))) {
          setProperty(all[0].id);
        }
      })
      .catch(err => alive && setPropsError(err.response?.data?.error || err.message));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Chargement du rapport ──
  const loadReport = useCallback(() => {
    if (!property) return;
    setLoading(true);
    setError(null);
    gaApi.getReport({ property, start: range.start, end: range.end, compare })
      .then(({ data }) => setReport(data))
      .catch(err => { setError(err.response?.data?.error || err.message); setReport(null); })
      .finally(() => setLoading(false));
  }, [property, range.start, range.end, compare]);

  useEffect(() => { if (tab !== 'audit') loadReport(); }, [loadReport, tab]);

  // ── Persistance des choix ──
  useEffect(() => { if (property) localStorage.setItem(LS_PROP, property); }, [property]);
  useEffect(() => { localStorage.setItem(LS_PRESET, preset); }, [preset]);

  // ── Temps réel (onglet actif → poll 30 s) ──
  useEffect(() => {
    if (tab !== 'realtime' || !property) { if (rtTimer.current) clearInterval(rtTimer.current); return; }
    const fetchRt = () => gaApi.getRealtime(property).then(({ data }) => setRealtime(data)).catch(() => {});
    fetchRt();
    rtTimer.current = setInterval(fetchRt, 30000);
    return () => { if (rtTimer.current) clearInterval(rtTimer.current); };
  }, [tab, property]);

  // ── Données dérivées pour le graphe principal ──
  const mainChartData = useMemo(() => {
    if (!report?.timeseries) return [];
    const prev = report.previousTimeseries || [];
    return report.timeseries.map((p, i) => ({
      date: p.date,
      value: p[mainMetric] || 0,
      prev: prev[i] ? (prev[i][mainMetric] != null ? prev[i][mainMetric] : (mainMetric === 'sessions' ? prev[i].sessions : prev[i].activeUsers)) : null,
    }));
  }, [report, mainMetric]);

  const currentPropName = useMemo(() => {
    for (const a of accounts) for (const p of a.properties) if (p.id === property) return p.name;
    return report?.property?.name || '';
  }, [accounts, property, report]);

  const metricDef = MAIN_METRICS.find(m => m.key === mainMetric) || MAIN_METRICS[0];
  const [actionsRefresh, setActionsRefresh] = useState(0);
  const [showQuickAction, setShowQuickAction] = useState(false);
  const { actions: siteActions, siteId: actionSiteId, siteName: actionSiteName } =
    useSiteActions('ga4', property, range.start, range.end, !!property, actionsRefresh);

  // ── Rendu ──
  if (configured === false) {
    return (
      <div className="page-container">
        <div className="ga-config-banner">
          <h2>📊 Google Analytics</h2>
          <p>Le connecteur Google Analytics n'est pas configuré sur ce serveur.</p>
          <p className="muted">Renseigner la clé du compte de service (<code>GA4_SA_KEY_FILE</code> ou <code>GA4_SA_KEY_JSON</code>) dans le <code>.env</code> du backend, puis redémarrer.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container ga-page">
      {/* 1. Onglets */}
      <div className="page-tabs">
        <button className={tab === 'overview' ? 'active' : ''} onClick={() => setTab('overview')}>📊 Vue d'ensemble</button>
        <button className={tab === 'acquisition' ? 'active' : ''} onClick={() => setTab('acquisition')}>🎯 Acquisition</button>
        <button className={tab === 'audience' ? 'active' : ''} onClick={() => setTab('audience')}>👥 Audience</button>
        <button className={tab === 'content' ? 'active' : ''} onClick={() => setTab('content')}>📄 Contenu</button>
        <button className={tab === 'realtime' ? 'active' : ''} onClick={() => setTab('realtime')}>⚡ Temps réel</button>
        <button className={tab === 'audit' ? 'active' : ''} onClick={() => setTab('audit')}>🤖 Audit IA</button>
        {isSuperAdmin && <button className={tab === 'config' ? 'active' : ''} onClick={() => setTab('config')}>⚙️ Configuration<span className="tab-sa-lock" title="Visible par les super-admins uniquement">🔒</span></button>}
      </div>

      {/* 2. Toolbar — sélection site + période (masquée sur l'onglet Configuration) */}
      {tab !== 'config' && (
      <div className="page-toolbar ga-toolbar">
        <select className="page-select ga-prop-select" value={property} onChange={e => setProperty(e.target.value)}>
          {accounts.map(acc => (
            <optgroup key={acc.account} label={acc.accountName}>
              {acc.properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </optgroup>
          ))}
        </select>

        {tab !== 'audit' && (
          <div className="ga-presets">
            {PRESETS.map(p => (
              <button key={p.id}
                className={`ga-preset ${preset === p.id ? 'active' : ''}`}
                onClick={() => setPreset(p.id)}>{p.label}</button>
            ))}
          </div>
        )}

        {tab !== 'audit' && preset === 'custom' && (
          <div className="ga-custom-dates">
            <input type="date" className="page-select" value={custom.start} max={custom.end}
              onChange={e => setCustom(c => ({ ...c, start: e.target.value }))} />
            <span>→</span>
            <input type="date" className="page-select" value={custom.end} min={custom.start}
              onChange={e => setCustom(c => ({ ...c, end: e.target.value }))} />
          </div>
        )}

        {tab !== 'audit' && (
          <>
            <button className={`ga-toggle ${compare ? 'active' : ''}`} onClick={() => setCompare(c => !c)}
              title="Comparer à la période précédente">⇄ Comparer</button>
            <button className="ga-icon-btn" onClick={loadReport} title="Rafraîchir">↻</button>
          </>
        )}
      </div>
      )}

      {/* Bandeau contextuel (masqué sur l'onglet Configuration) */}
      {tab !== 'config' && (
      <div className="ga-context">
        <span className="ga-context-site">{currentPropName || '—'}</span>
        <span className="ga-context-range">
          {tab === 'audit' ? '30 derniers jours · audit de la mesure' : (
            <>
              {range.start} → {range.end}
              {compare && report?.previous && <span className="muted"> · vs {report.previous.start} → {report.previous.end}</span>}
            </>
          )}
        </span>
        {tab !== 'audit' && report?.property?.timeZone && <span className="muted ga-context-tz">🕓 {report.property.timeZone}</span>}
      </div>
      )}

      {tab !== 'config' && propsError && <div className="ga-error">⚠️ {propsError}</div>}
      {tab !== 'config' && error && <div className="ga-error">⚠️ {error}</div>}
      {tab !== 'config' && report?.warnings?.length > 0 && (
        <div className="ga-warn">Certaines sections n'ont pas pu être chargées : {report.warnings.join(', ')}.</div>
      )}

      {/* ── ONGLET CONFIGURATION (super-admin) ── */}
      {tab === 'config' && isSuperAdmin && <GaConfigTab />}

      {loading && !report && tab !== 'audit' && tab !== 'config' && <div className="ga-loading">Chargement des données Google Analytics…</div>}

      {/* ── ONGLET AUDIT IA ── */}
      {tab === 'audit' && <GaAuditTab property={property} propertyName={currentPropName} />}

      {tab !== 'audit' && report && (
        <>
          {/* ── VUE D'ENSEMBLE ── */}
          {tab === 'overview' && (
            <>
              <div className="ga-kpi-grid">
                {KPI_DEFS.map(def => (
                  <KpiCard key={def.key} def={def} kpi={report.kpis[def.key]} chartData={report.timeseries} />
                ))}
              </div>

              <Panel
                title="Évolution dans le temps"
                subtitle={compare ? 'Trait plein = période courante · pointillé = période précédente' : null}
              >
                <div className="ga-metric-switch">
                  {MAIN_METRICS.map(m => (
                    <button key={m.key}
                      className={`ga-chip ${mainMetric === m.key ? 'active' : ''}`}
                      style={mainMetric === m.key ? { borderColor: m.color, color: m.color } : null}
                      onClick={() => setMainMetric(m.key)}>{m.label}</button>
                  ))}
                  <button className="ga-chip" title="Consigner une action menée sur ce site" style={{ marginLeft: 'auto' }} onClick={() => setShowQuickAction(true)}>+ Action</button>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={mainChartData} margin={{ top: 10, right: 16, left: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gaMainFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={metricDef.color} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={metricDef.color} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} minTickGap={24} />
                    <YAxis tickFormatter={fmtCompact} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} width={44} />
                    <Tooltip content={<MainChartTooltip metricLabel={metricDef.label} compare={compare} actionsByDate={groupMarkersByDate(actionMarkers(siteActions, mainChartData.map(p => p.date)))} />} />
                    {compare && (
                      <Line type="monotone" dataKey="prev" stroke="var(--text-muted)" strokeWidth={1.5} strokeDasharray="4 4" dot={false} isAnimationActive={false} />
                    )}
                    <Area type="monotone" dataKey="value" stroke={metricDef.color} strokeWidth={2.5} fill="url(#gaMainFill)" dot={false} isAnimationActive={false} />
                    {actionMarkers(siteActions, mainChartData.map(p => p.date)).map(m => (
                      <ReferenceLine key={m.key} x={m.x} stroke={m.color} strokeDasharray="4 4"
                        label={{ value: m.emoji, position: 'top', fontSize: 12 }} />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
              </Panel>
              {showQuickAction && (
                <QuickActionModal siteId={actionSiteId} siteName={actionSiteName} defaultChannel="ga4"
                  onClose={() => setShowQuickAction(false)}
                  onSaved={() => { setShowQuickAction(false); setActionsRefresh(x => x + 1); }} />
              )}

              <div className="ga-grid-2">
                <Panel title="Canaux d'acquisition" subtitle="par sessions">
                  <DonutChart data={report.channels} dataKey="sessions" />
                </Panel>
                <Panel title="Appareils" subtitle="par sessions">
                  <DonutChart data={report.devices} dataKey="sessions" />
                </Panel>
              </div>
            </>
          )}

          {/* ── ACQUISITION ── */}
          {tab === 'acquisition' && (
            <>
              <div className="ga-grid-2">
                <Panel title="Canaux" subtitle="répartition des sessions">
                  <DonutChart data={report.channels} dataKey="sessions" />
                </Panel>
                <Panel title="Canaux — détail">
                  <BreakdownTable rows={report.channels} cols={{
                    labelHead: 'Canal',
                    metrics: [
                      { key: 'sessions', head: 'Sessions', fmt: fmtNumber },
                      { key: 'users', head: 'Utilisateurs', fmt: fmtNumber },
                    ],
                  }} />
                </Panel>
              </div>
              <Panel title="Sources / Support" subtitle="Top 10 par sessions">
                <BreakdownTable rows={report.sources} cols={{
                  labelHead: 'Source / Support',
                  metrics: [
                    { key: 'sessions', head: 'Sessions', fmt: fmtNumber },
                    { key: 'users', head: 'Utilisateurs', fmt: fmtNumber },
                  ],
                }} />
              </Panel>
            </>
          )}

          {/* ── AUDIENCE ── */}
          {tab === 'audience' && (
            <>
              <div className="ga-grid-2">
                <Panel title="Appareils" subtitle="par sessions">
                  <DonutChart data={report.devices} dataKey="sessions" />
                </Panel>
                <Panel title="Pays" subtitle="Top 8 par utilisateurs">
                  {report.countries.length === 0 ? <div className="ga-empty">Aucune donnée</div> : (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={report.countries} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" horizontal={false} />
                        <XAxis type="number" tickFormatter={fmtCompact} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                        <YAxis type="category" dataKey="label" width={90} tick={{ fontSize: 11, fill: 'var(--text-primary)' }} />
                        <Tooltip formatter={(v) => fmtNumber(v)} contentStyle={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 12 }} />
                        <Bar dataKey="users" fill="var(--color-accent)" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Panel>
              </div>
              <Panel title="Pays — détail">
                <BreakdownTable rows={report.countries} cols={{
                  labelHead: 'Pays',
                  metrics: [
                    { key: 'users', head: 'Utilisateurs', fmt: fmtNumber },
                    { key: 'sessions', head: 'Sessions', fmt: fmtNumber },
                  ],
                }} />
              </Panel>
            </>
          )}

          {/* ── CONTENU ── */}
          {tab === 'content' && (
            <>
              <Panel title="Pages les plus vues" subtitle="Top 12">
                <BreakdownTable rows={report.topPages} cols={{
                  labelHead: 'Page',
                  metrics: [
                    { key: 'views', head: 'Pages vues', fmt: fmtNumber },
                    { key: 'users', head: 'Utilisateurs', fmt: fmtNumber },
                  ],
                }} />
              </Panel>
              <div className="ga-grid-2">
                <Panel title="Pages d'entrée" subtitle="Top 10 par sessions">
                  <BreakdownTable rows={report.landingPages} cols={{
                    labelHead: 'Landing page',
                    metrics: [
                      { key: 'sessions', head: 'Sessions', fmt: fmtNumber },
                      { key: 'engagementRate', head: 'Engagement', fmt: fmtPercent },
                    ],
                  }} />
                </Panel>
                <Panel title="Événements" subtitle="Top 12">
                  <BreakdownTable rows={report.events} cols={{
                    labelHead: 'Événement',
                    metrics: [
                      { key: 'count', head: 'Nombre', fmt: fmtNumber },
                      { key: 'users', head: 'Utilisateurs', fmt: fmtNumber },
                    ],
                  }} />
                </Panel>
              </div>
            </>
          )}

          {/* ── TEMPS RÉEL ── */}
          {tab === 'realtime' && (
            <>
              <div className="ga-realtime-hero">
                <div className="ga-rt-pulse" />
                <div>
                  <div className="ga-rt-num">{fmtNumber(realtime?.activeUsers || 0)}</div>
                  <div className="ga-rt-cap">utilisateurs actifs en ce moment</div>
                </div>
                <div className="muted ga-rt-note">Actualisation automatique toutes les 30 s · 30 dernières minutes</div>
              </div>
              <div className="ga-grid-2">
                <Panel title="Pages actives">
                  <BreakdownTable rows={realtime?.pages} empty="Aucun visiteur actif" cols={{
                    labelHead: 'Page / écran',
                    metrics: [{ key: 'users', head: 'Actifs', fmt: fmtNumber }],
                  }} />
                </Panel>
                <Panel title="Appareils">
                  {realtime?.devices?.length ? <DonutChart data={realtime.devices} dataKey="users" height={220} />
                    : <div className="ga-empty">Aucun visiteur actif</div>}
                </Panel>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default GoogleAnalytics;
